import React, {useState, useEffect, useMemo, useCallback, useLayoutEffect, useRef} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {useLanguage} from '../../../_gori/i18n/Metronici18n'
import LanguageToggle from '../../modules/common/components/LanguageToggle'
import DemoPanel from '../../modules/demo/DemoPanel'
import {
  DEMO_TOPICS,
  DEMO_ALL_SUBTOPICS,
  DEMO_SUBTOPIC_INDEX,
  buildDemoVideoUrl,
} from '../../modules/demo/_demoConfig'
import {SeoHead} from '../../modules/seo'

interface TopicsLocationState {
  /** Subtopic keys (i18n keys) to pre-select on mount. */
  preSelectedSubtopicKeys?: string[]
  autoStart?: boolean
  /** Subtopic keys newly added in the finish modal — playback starts at the first of these. */
  newlyAddedSubtopicKeys?: string[]
}

const DemoTopicsPage: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const intl = useIntl()
  const {language: currentLanguage} = useLanguage()

  // Map language to supported video language codes
  const language = useMemo(() => {
    if (currentLanguage.startsWith('ja')) return 'ja'
    if (currentLanguage.startsWith('zh')) return 'zh'
    return 'en'
  }, [currentLanguage])

  const locationState = location.state as TopicsLocationState
  const autoStart = locationState?.autoStart || false
  const newlyAddedSubtopicKeys = useMemo(
    () => locationState?.newlyAddedSubtopicKeys || [],
    [locationState?.newlyAddedSubtopicKeys]
  )

  const [selectedSubtopicKeys, setSelectedSubtopicKeys] = useState<string[]>(
    () => locationState?.preSelectedSubtopicKeys || []
  )

  /** subtopicKey → "X min Y sec" */
  const [videoDurations, setVideoDurations] = useState<Record<string, string>>({})

  /**
   * Per-topic expansion state. Defaults to all collapsed so users get a clean
   * overview list and can drill into individual topics. The left column has
   * a fixed viewport height with the cards in a scroll region, so collapsing
   * by default keeps everything visible without scrolling.
   */
  const [expandedTopics, setExpandedTopics] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(DEMO_TOPICS.map((t) => [t.key, false]))
  )

  /**
   * Per-topic "all subtopics fit on a single row?" state. Computed by measuring
   * `offsetTop` of every pill within the topic's row container — if all share
   * the same `offsetTop`, no wrapping occurred → single-row layout. Otherwise
   * the row layout has overflowed and we switch the topic to a stacked column.
   *
   * `null` = not yet measured (first paint default of `true` so we render row,
   * then re-measure synchronously in a layout effect).
   */
  const [singleRowTopics, setSingleRowTopics] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(DEMO_TOPICS.map((t) => [t.key, true]))
  )

  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.floor(seconds % 60)
    if (minutes > 0) {
      return `${minutes} min ${remainingSeconds} sec`
    }
    return `${remainingSeconds} sec`
  }

  const getVideoDuration = (url: string): Promise<number> => {
    return new Promise((resolve) => {
      const video = document.createElement('video')
      video.preload = 'metadata'
      video.onloadedmetadata = () => resolve(video.duration)
      // Fallback to 60s for unavailable videos so the demo still computes a total.
      video.onerror = () => resolve(60)
      video.src = url
    })
  }

  // Fetch durations for all configured subtopics in parallel on language change.
  useEffect(() => {
    let cancelled = false
    const fetchDurations = async () => {
      setVideoDurations({})
      const entries = await Promise.all(
        DEMO_ALL_SUBTOPICS.map(async (subtopic) => {
          const url = buildDemoVideoUrl(subtopic.videoPath, language)
          const seconds = await getVideoDuration(url)
          return [subtopic.key, formatDuration(seconds)] as const
        })
      )
      if (!cancelled) {
        setVideoDurations(Object.fromEntries(entries))
      }
    }
    fetchDurations()
    return () => {
      cancelled = true
    }
  }, [language])

  /** Sum of durations for the currently selected subtopics (formatted). */
  const totalDuration = useMemo(() => {
    const totalSeconds = selectedSubtopicKeys.reduce((total, key) => {
      const duration = videoDurations[key]
      if (!duration) return total
      // Parse "X min Y sec" or "Y sec"
      const match = duration.match(/(?:(\d+)\s*min)?\s*(\d+)\s*sec/)
      if (!match) return total
      const minutes = parseInt(match[1] || '0', 10)
      const seconds = parseInt(match[2], 10)
      return total + minutes * 60 + seconds
    }, 0)
    return formatDuration(totalSeconds)
  }, [selectedSubtopicKeys, videoDurations])

  const handleTopicToggle = useCallback((topicKey: string) => {
    const topic = DEMO_TOPICS.find((t) => t.key === topicKey)
    if (!topic) return
    const topicSubtopicKeys = topic.subtopics.map((s) => s.key)
    setSelectedSubtopicKeys((prev) => {
      const allSelected = topicSubtopicKeys.every((k) => prev.includes(k))
      if (allSelected) {
        return prev.filter((k) => !topicSubtopicKeys.includes(k))
      }
      // Add only the keys that aren't already selected, preserve existing order.
      const toAdd = topicSubtopicKeys.filter((k) => !prev.includes(k))
      return [...prev, ...toAdd]
    })
  }, [])

  const handleSubtopicToggle = useCallback((subtopicKey: string) => {
    setSelectedSubtopicKeys((prev) => {
      if (prev.includes(subtopicKey)) {
        return prev.filter((k) => k !== subtopicKey)
      }
      return [...prev, subtopicKey]
    })
  }, [])

  const handleExpandToggle = useCallback((topicKey: string) => {
    setExpandedTopics((prev) => ({...prev, [topicKey]: !prev[topicKey]}))
  }, [])

  const handleStartDemo = useCallback(() => {
    // Enforce playback order = config order, regardless of how user clicked.
    const orderedKeys = DEMO_ALL_SUBTOPICS.filter((s) => selectedSubtopicKeys.includes(s.key)).map(
      (s) => s.key
    )

    const selectedVideos = orderedKeys.map((key) => {
      const entry = DEMO_SUBTOPIC_INDEX[key]
      return {
        key,
        title: intl.formatMessage({id: key}),
        url: buildDemoVideoUrl(entry.subtopic.videoPath, language),
        duration: videoDurations[key] || intl.formatMessage({id: 'DEMO_TOPICS_LOADING'}),
        topicKey: entry.topic.key,
      }
    })

    // Find playback start index: first newly-added key (if any), else 0.
    let startIndex = 0
    if (newlyAddedSubtopicKeys.length > 0) {
      const idx = orderedKeys.indexOf(newlyAddedSubtopicKeys[0])
      if (idx >= 0) startIndex = idx
    }

    navigate('/demo/demo-player', {
      state: {
        videos: selectedVideos,
        totalDuration,
        selectedSubtopicKeys: orderedKeys,
        startVideoIndex: startIndex,
      },
    })
  }, [
    selectedSubtopicKeys,
    intl,
    language,
    videoDurations,
    newlyAddedSubtopicKeys,
    navigate,
    totalDuration,
  ])

  // Auto-start once durations have settled.
  useEffect(() => {
    if (!autoStart || selectedSubtopicKeys.length === 0) return
    const allLoaded = selectedSubtopicKeys.every((key) => Boolean(videoDurations[key]))
    if (allLoaded) handleStartDemo()
  }, [autoStart, selectedSubtopicKeys, videoDurations, handleStartDemo])

  // ---------------------------------------------------------------------------
  // All-or-each subtopic layout detection
  // ---------------------------------------------------------------------------
  // Strategy: pills always render with `flex-wrap: wrap`. After commit, we
  // measure each pill's `offsetTop`. If all share the same top, no wrapping
  // happened → keep row layout. If any pill wraps to a new line → switch to
  // stacked (each pill takes 100% width, one per visual row).
  //
  // CRITICAL EDGE CASE: once a topic is in stacked mode, every pill naturally
  // has a different `offsetTop` (each on its own line), so naive re-measuring
  // would always report "wrapped" and never recover when the user widens the
  // window. To get an honest "would this fit in row mode?" answer we must
  // temporarily force row mode in the DOM, force a synchronous reflow, then
  // read `offsetTop`. Because this happens inside `useLayoutEffect` (which
  // blocks paint), there is zero visual flicker.
  // ---------------------------------------------------------------------------

  const subtopicRowRefs = useRef<Record<string, HTMLDivElement | null>>({})
  const [resizeToken, setResizeToken] = useState(0)

  const measureLayout = useCallback(() => {
    const ROW_CLASS = 'demo-subtopics-grid--row'
    const STACKED_CLASS = 'demo-subtopics-grid--stacked'

    const measurements: Record<string, boolean> = {}

    DEMO_TOPICS.forEach((topic) => {
      const row = subtopicRowRefs.current[topic.key]
      if (!row) return

      const pills = Array.from(row.children) as HTMLElement[]
      if (pills.length <= 1) {
        measurements[topic.key] = true
        return
      }

      // Force row mode for measurement. Restore later if state doesn't update
      // the class via re-render. Inside useLayoutEffect → no visible flicker.
      const wasStacked = row.classList.contains(STACKED_CLASS)
      if (wasStacked) {
        row.classList.remove(STACKED_CLASS)
        row.classList.add(ROW_CLASS)
        // Force synchronous reflow so offsetTop reflects row-mode layout.
        void row.offsetHeight
      }

      const firstTop = pills[0].offsetTop
      const wrapped = pills.some((p) => p.offsetTop !== firstTop)
      measurements[topic.key] = !wrapped

      // If state will keep this topic in stacked mode (wrapped) but DOM was
      // mutated to row, re-apply stacked class so React's view matches the
      // DOM and we don't paint a broken layout.
      if (wasStacked && wrapped) {
        row.classList.remove(ROW_CLASS)
        row.classList.add(STACKED_CLASS)
      }
    })

    setSingleRowTopics((prev) => {
      let changed = false
      const next: Record<string, boolean> = {...prev}
      Object.entries(measurements).forEach(([k, v]) => {
        if (next[k] !== v) {
          next[k] = v
          changed = true
        }
      })
      return changed ? next : prev
    })
  }, [])

  // Re-measure when content widths or container width could have changed:
  // - language: pill text widths change up to ~30% across en/zh/ja
  // - expandedTopics: newly-expanded rows need first measurement
  // - resizeToken: bumped on window resize
  useLayoutEffect(() => {
    measureLayout()
  }, [language, expandedTopics, resizeToken, measureLayout])

  // One shared resize listener for all topics. rAF debounce avoids flooding
  // measurements during drag-resize.
  useEffect(() => {
    let raf = 0
    const onResize = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(() => {
        setResizeToken((t) => t + 1)
      })
    }
    window.addEventListener('resize', onResize, {passive: true})
    return () => {
      window.removeEventListener('resize', onResize)
      cancelAnimationFrame(raf)
    }
  }, [])

  return (
    <div className='demo-topics-container'>
      <SeoHead />
      {/* Left Side */}
      <div className='demo-topics-left'>
        <div className='demo-content-container demo-topics-content'>
          {/* Fixed header: logo + title (never scrolls) */}
          <div className='demo-topics-header'>
            <img
              src='/media/logos/shipbae-logo-black.svg'
              alt='ShipBae Powered By Gori.ai'
              className='demo-logo'
            />

            <h1 className='demo-topics-title'>{intl.formatMessage({id: 'DEMO_TOPICS_TITLE'})}</h1>
          </div>

          {/* Scrollable cards region — flex-grows to fill available height,
              cards inside scroll independently of the fixed header/footer */}
          <div className='demo-topics-cards-scroll'>
            {DEMO_TOPICS.map((topic) => {
              const topicSubtopicKeys = topic.subtopics.map((s) => s.key)
              const isTopicSelected = topicSubtopicKeys.every((k) =>
                selectedSubtopicKeys.includes(k)
              )
              const isExpanded = expandedTopics[topic.key]
              const isSingleRow = singleRowTopics[topic.key]
              return (
                <div className='demo-topics-card p-4' key={topic.key}>
                  {/* Topic header */}
                  <div className='d-flex align-items-center justify-content-between mb-0'>
                    <div className='d-flex align-items-center flex-grow-1'>
                      <div
                        className={`demo-checkbox ${isTopicSelected ? 'checked' : ''}`}
                        onClick={() => handleTopicToggle(topic.key)}
                      />
                      <span className='demo-main-topic-text'>
                        {intl.formatMessage({id: topic.key})}
                      </span>
                    </div>
                    <span className='demo-subtopics-count me-3'>
                      {intl.formatMessage(
                        {id: 'DEMO_SUB_TOPICS_COUNT'},
                        {count: topic.subtopics.length}
                      )}
                    </span>
                    <button
                      type='button'
                      className='demo-topic-toggle-btn'
                      onClick={(e) => {
                        e.stopPropagation()
                        handleExpandToggle(topic.key)
                      }}
                      aria-expanded={isExpanded}
                      aria-label={intl.formatMessage({
                        id: isExpanded ? 'DEMO_TOPIC_TOGGLE_COLLAPSE' : 'DEMO_TOPIC_TOGGLE_EXPAND',
                      })}
                    >
                      {isExpanded ? (
                        // Minus icon (16x16)
                        <svg
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                          fill='none'
                          aria-hidden='true'
                        >
                          <rect x='2' y='7' width='12' height='2' rx='1' fill='#475569' />
                        </svg>
                      ) : (
                        // Plus icon (16x16)
                        <svg
                          width='16'
                          height='16'
                          viewBox='0 0 16 16'
                          fill='none'
                          aria-hidden='true'
                        >
                          <rect x='2' y='7' width='12' height='2' rx='1' fill='#475569' />
                          <rect x='7' y='2' width='2' height='12' rx='1' fill='#475569' />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Sub-topics — only rendered when expanded */}
                  {isExpanded && (
                    <div className='demo-topic-subtopics-section'>
                      <div className='d-flex align-items-start mt-3'>
                        <img
                          src='/media/gori/demo/subtopic-arrow.svg'
                          alt=''
                          aria-hidden='true'
                          className='demo-subtopic-arrow'
                        />
                        <div className='flex-grow-1'>
                          <div className='demo-subtopics-label mb-2'>
                            {intl.formatMessage({id: 'DEMO_TOPICS_SUB_TOPICS_LABEL'})}
                          </div>
                          <div
                            ref={(el) => {
                              subtopicRowRefs.current[topic.key] = el
                            }}
                            className={`demo-subtopics-grid ${
                              isSingleRow
                                ? 'demo-subtopics-grid--row'
                                : 'demo-subtopics-grid--stacked'
                            }`}
                          >
                            {topic.subtopics.map((subtopic) => (
                              <div
                                key={subtopic.key}
                                className='demo-subtopic-container'
                                onClick={() => handleSubtopicToggle(subtopic.key)}
                              >
                                <div
                                  className={`demo-checkbox-small ${
                                    selectedSubtopicKeys.includes(subtopic.key) ? 'checked' : ''
                                  }`}
                                />
                                <span className='demo-subtopic-text'>
                                  {intl.formatMessage({id: subtopic.key})}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Fixed Back button — stays anchored at the bottom of the column,
              never scrolls with the cards. */}
          <button
            onClick={() => navigate('/demo/intro-video')}
            className='btn d-flex align-items-center gap-1 demo-back-topics'
          >
            {intl.formatMessage({id: 'DEMO_BACK_BUTTON'})}
          </button>
        </div>
      </div>

      {/* Right Side */}
      <div className='demo-topics-right'>
        <DemoPanel
          key={`topics-${language}`}
          mode='topics'
          totalDuration={totalDuration}
          videosCount={selectedSubtopicKeys.length}
          selectedSubtopicKeys={selectedSubtopicKeys}
          videoDurations={videoDurations}
        />

        <div className='mt-auto'>
          <button
            onClick={handleStartDemo}
            disabled={selectedSubtopicKeys.length === 0}
            className={`demo-start-button w-100 ${
              selectedSubtopicKeys.length > 0 ? 'enabled' : 'disabled'
            }`}
          >
            {intl.formatMessage({id: 'DEMO_TOPICS_START_DEMO'})}
          </button>
        </div>
      </div>

      <LanguageToggle allowedCodes={['en', 'zh']} />
    </div>
  )
}

export default DemoTopicsPage
